import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  App as AntApp,
  Button,
  Descriptions,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Space,
  Spin,
  Timeline,
  Typography,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { errorMessage } from '../api';
import type { BillingApi, TimelineEvent, UserDetail } from '../billingApi';
import { StatusTag } from '../components/StatusTag';
import { day, dayTime, daysFromNow } from '../lib/format';
import type { Route } from '../router';

const { Text } = Typography;

const KIND_COLOR: Record<TimelineEvent['kind'], string> = {
  signup: 'blue',
  trial: 'blue',
  subscription: 'green',
  payment: 'green',
  refund: 'orange',
  chargeback: 'red',
  grant: 'purple',
  email: 'gray',
};

/** What the learner may do now, in a few words. */
function accessText(detail: UserDetail): string {
  const a = detail.access;
  switch (a.state) {
    case 'active':
      return a.until ? `Subscribed, ends ${day(a.until)}` : 'Subscribed';
    case 'grant':
      return a.until ? `Free access until ${day(a.until)}` : 'Free access, no end';
    case 'trial':
      return `Trial until ${day(a.until)}`;
    default:
      return a.locked_since ? `Locked since ${day(a.locked_since)}` : 'Locked';
  }
}

const hasRunningGrant = (detail: UserDetail, now = Date.now()) =>
  detail.grants.some(
    (g) =>
      !g.revoked_at && (!g.ends_at || Date.parse(g.ends_at) > now) && Date.parse(g.starts_at) <= now
  );

const tomorrow = () => new Date(Date.now() + 86400000).toISOString().slice(0, 10);

/** One account: status, what it may do, a timeline, and the three owner actions. */
export const UserScreen: React.FC<{
  billing: BillingApi;
  userId: string;
  navigate: (route: Route) => void;
}> = ({ billing, userId, navigate }) => {
  const { message } = AntApp.useApp();
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [dialog, setDialog] = useState<'trial' | 'grant' | null>(null);
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState<number | null>(7);
  const [forever, setForever] = useState(false);
  const [until, setUntil] = useState(tomorrow);
  const [reason, setReason] = useState('');

  useEffect(() => {
    let cancelled = false;
    billing
      .getUser(userId)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setLoadError(null);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(errorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [billing, userId, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const run = async (work: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await work();
      message.success(done);
      setDialog(null);
      reload();
    } catch (e) {
      message.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const back = (
    <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate({ name: 'users' })}>
      Users
    </Button>
  );

  if (loadError) {
    return (
      <div>
        {back}
        <Alert
          type="error"
          showIcon
          title="Could not load this user"
          description={loadError}
          action={<Button onClick={reload}>Try again</Button>}
        />
      </div>
    );
  }
  if (!detail) {
    return (
      <div>
        {back}
        <Spin style={{ display: 'block', marginTop: 48 }} />
      </div>
    );
  }

  const left = daysFromNow(detail.billing?.trial_ends_at);
  const sub = detail.access.subscription;
  const grantReady = !!reason.trim() && (forever || /^\d{4}-\d{2}-\d{2}$/.test(until));

  return (
    <div>
      {back}
      <Space align="center" wrap style={{ margin: '4px 0 16px' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {detail.user.email ?? '(no email)'}
        </Typography.Title>
        <StatusTag status={detail.status} />
      </Space>

      <Descriptions
        bordered
        size="small"
        column={{ xs: 1, md: 2 }}
        style={{ background: '#fff', marginBottom: 16 }}
        items={[
          { key: 'access', label: 'Access', children: accessText(detail) },
          {
            key: 'trial',
            label: 'Trial ends',
            children: detail.billing ? (
              <>
                {day(detail.billing.trial_ends_at)}
                {left !== null && left > 0 && <Text type="secondary"> · {left} days left</Text>}
              </>
            ) : (
              ''
            ),
          },
          {
            key: 'sub',
            label: 'Subscription',
            children: sub ? (
              <>
                {sub.plan}, {sub.status}
                {sub.current_period_end && (
                  <Text type="secondary"> · period ends {day(sub.current_period_end)}</Text>
                )}
              </>
            ) : (
              <Text type="secondary">None</Text>
            ),
          },
          { key: 'signup', label: 'Signed up', children: day(detail.user.created_at) },
          {
            key: 'signin',
            label: 'Last sign-in',
            children: day(detail.user.last_sign_in_at) || 'Never',
          },
          {
            key: 'activity',
            label: 'Last activity',
            children: day(detail.last_activity_at) || 'None',
          },
        ]}
      />

      <Space wrap style={{ marginBottom: 24 }}>
        <Button onClick={() => setDialog('trial')}>Extend trial</Button>
        <Button onClick={() => setDialog('grant')}>Give free access</Button>
        {hasRunningGrant(detail) && (
          <Popconfirm
            title="Take back the free access?"
            okText="Take back"
            onConfirm={() => run(() => billing.revokeGrant(userId), 'Free access taken back')}
          >
            <Button danger>Take back</Button>
          </Popconfirm>
        )}
      </Space>

      <Timeline
        items={detail.timeline.map((e, i) => ({
          key: `${e.at}-${i}`,
          color: KIND_COLOR[e.kind] ?? 'gray',
          title: <Text type="secondary">{dayTime(e.at)}</Text>,
          content: (
            <>
              {e.title}
              {e.detail && <Text type="secondary"> · {e.detail}</Text>}
            </>
          ),
        }))}
      />

      <Modal
        open={dialog === 'trial'}
        title="Extend trial"
        okText="Extend"
        confirmLoading={busy}
        okButtonProps={{ disabled: !days }}
        onCancel={() => setDialog(null)}
        onOk={() => run(() => billing.extendTrial(userId, days!), 'Trial extended')}
        destroyOnHidden
      >
        <Space>
          <InputNumber
            min={1}
            max={365}
            precision={0}
            value={days}
            onChange={setDays}
            aria-label="Days"
            style={{ width: 100 }}
          />
          <Text>days</Text>
        </Space>
      </Modal>

      <Modal
        open={dialog === 'grant'}
        title="Give free access"
        okText="Give access"
        confirmLoading={busy}
        okButtonProps={{ disabled: !grantReady }}
        onCancel={() => setDialog(null)}
        onOk={() =>
          run(
            () => billing.grant(userId, forever ? null : `${until}T23:59:59Z`, reason.trim()),
            'Free access given'
          )
        }
        destroyOnHidden
      >
        <Space orientation="vertical" style={{ width: '100%' }}>
          <Radio.Group
            value={forever ? 'forever' : 'until'}
            onChange={(e) => setForever(e.target.value === 'forever')}
            options={[
              { value: 'until', label: 'Until' },
              { value: 'forever', label: 'No end' },
            ]}
          />
          {!forever && (
            <Input
              type="date"
              aria-label="Until"
              value={until}
              min={tomorrow()}
              onChange={(e) => setUntil(e.target.value)}
              style={{ width: 180 }}
            />
          )}
          <Input.TextArea
            rows={2}
            maxLength={500}
            placeholder="Reason"
            aria-label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Space>
      </Modal>
    </div>
  );
};
